import {
  FrequencyDurationUnit,
  ContractRecurringMode,
  ContractQuantityCalculationType,
  ContractType,
  ServiceTypeItemOfferingType,
  PackageTypeItemOfferingType
} from '@nile/types';
import {
  CREATE_CLIENT_CONTRACT_NEW,
  CREATE_CONTRACT_TEMPLATE_NEW,
  DELETE_CLIENT_CONTRACT_NEW,
  DELETE_CONTRACT_TEMPLATE_NEW,
  FETCH_CLIENT_CONTRACT,
  GET_CONTRACT_TEMPLATE,
  UPDATE_CLIENT_CONTRACT_NEW,
  UPDATE_CONTRACT_TEMPLATE_NEW
} from '@nile/graphql';
import {
  GET_CLIENT_PROVISION_RULE_LIST,
  GET_TEMPLATE_PROVISION_RULE_LIST
} from '@nile/graphql/src/Query/contracts';
import {
  CREATE_CLIENT_PROVISION_RULE,
  CREATE_TEMPLATE_PROVISION_RULE
} from '@nile/graphql/src/Mutation/contracts';

export const CONTRACT_API_MAP = {
  clientContract: {
    fetchContractQuery: FETCH_CLIENT_CONTRACT,
    fetchContractKey: 'fetchClientContract',
    createContractQuery: CREATE_CLIENT_CONTRACT_NEW,
    createContractKey: 'createClientBillingContract',
    updateContractQuery: UPDATE_CLIENT_CONTRACT_NEW,
    updateContractKey: 'updateClientBillingContract',
    deleteContractQuery: DELETE_CLIENT_CONTRACT_NEW,
    deleteContractKey: 'deleteClientContract',
    getProvisionRuleQuery: GET_CLIENT_PROVISION_RULE_LIST,
    getProvisionRuleKey: 'getClientProvisionRuleList',
    createProvisionRuleQuery: CREATE_CLIENT_PROVISION_RULE,
    createProvisionRuleKey: 'createClientProvisionRule'
  },
  contractTemplate: {
    fetchContractQuery: GET_CONTRACT_TEMPLATE,
    fetchContractKey: 'getContractTemplate',
    createContractQuery: CREATE_CONTRACT_TEMPLATE_NEW,
    createContractKey: 'createBillingContractTemplate',
    updateContractQuery: UPDATE_CONTRACT_TEMPLATE_NEW,
    updateContractKey: 'updateBillingContractTemplate',
    deleteContractQuery: DELETE_CONTRACT_TEMPLATE_NEW,
    deleteContractKey: 'deleteBillingContractTemplate',
    getProvisionRuleQuery: GET_TEMPLATE_PROVISION_RULE_LIST,
    getProvisionRuleKey: 'getProvisionRuleTemplateList',
    createProvisionRuleQuery: CREATE_TEMPLATE_PROVISION_RULE,
    createProvisionRuleKey: 'createProvisionRuleTemplate'
  }
};

export const FREQUENCY_DURATION_UNITS: {
  name: FrequencyDurationUnit;
  value: FrequencyDurationUnit;
  label: string;
}[] = [
  {
    name: 'DAY',
    value: 'DAY',
    label: 'attribute.days'
  },
  {
    name: 'WEEK',
    value: 'WEEK',
    label: 'common.week'
  },
  {
    name: 'MONTH',
    value: 'MONTH',
    label: 'attribute.month'
  },
  {
    name: 'YEAR',
    value: 'YEAR',
    label: 'attribute.year'
  }
];

export const RECURRING_MODES: Record<
  ContractRecurringMode,
  { value: ContractRecurringMode; label: string; info: string }
> = {
  UPFRONT: {
    value: 'UPFRONT',
    label: 'client.details.contractinvoice.recurringMode.upfront',
    info: 'client.details.contractinvoice.recurringMode.upfront.info'
  },
  ARREAR: {
    value: 'ARREAR',
    label: 'client.details.contractinvoice.recurringMode.arrear',
    info: 'client.details.contractinvoice.recurringMode.arrear.info'
  },
  ADVANCE: {
    value: 'ADVANCE',
    label: 'client.details.contractinvoice.recurringMode.advanced',
    info: 'client.details.contractinvoice.recurringMode.advanced.info'
  }
};

export const CONTRACT_TYPE_KEYS: Record<
  ContractType,
  {
    value: ContractType;
    label: string;
    info: string;
    contractLabel?: string;
    whatsThis?: string;
    featureGuideKey: string;
  }
> = {
  SERVICE: {
    value: 'SERVICE',
    label: 'Recurring',
    contractLabel: 'msp.contracts.type.recurring.contractLabel',
    // newContractLabel: 'contract.type.recurring.contractLabel',
    info: 'client.details.contractinvoice.recurringMode.upfront.info',
    whatsThis: 'msp.contracts.whatIsRecurring',
    featureGuideKey: 'recurring'
  },
  ONE_TIME: {
    value: 'ONE_TIME',
    label: 'Project',
    contractLabel: 'msp.contracts.type.onetime.contractLabel',
    // newContractLabel: 'contract.type.onetime.contractLabel',
    info: 'client.details.contractinvoice.recurringMode.arrear.info',
    whatsThis: 'msp.contracts.whatsThis.oneTime',
    featureGuideKey: 'oneTime'
  },
  USAGE: {
    value: 'USAGE',
    label: 'Service',
    contractLabel: 'msp.contracts.type.usage.contractLabel',
    // newContractLabel: 'contract.type.usage.contractLabel',
    info: 'client.details.contractinvoice.recurringMode.advanced.info',
    whatsThis: 'msp.contracts.whatIsUsage',
    featureGuideKey: 'usageBased'
  },
  TIME_AND_MATERIAL: {
    value: 'TIME_AND_MATERIAL',
    label: 'Time & Material',
    contractLabel: 'msp.contracts.type.time.and.material.contractLabel',
    // newContractLabel: 'contract.type.time.and.material.contractLabel',
    info: 'client.details.contractinvoice.recurringMode.advanced.info',
    whatsThis: 'msp.contracts.whatIsTimeAndMaterial',
    featureGuideKey: 'timeAndMaterial'
  }
};

export const QUANTITY_CALCULATION_TYPES: {
  value: ContractQuantityCalculationType;
  label: string;
}[] = [
  {
    label: 'Dynamic',
    value: 'DYNAMIC'
  },
  {
    value: 'FIXED',
    label: 'Fixed'
  }
];

export const QUANTITY_OPTIONS = [
  {
    title: 'SITE',
    options: [
      {
        value: 'ALL_SITE',
        label: '123456'
      }
    ]
  }
];

export const ServiceCatalogConditionsBasedOnContractType: Record<
  ContractType,
  any
> = {
  SERVICE: {
    joinOperator: 'AND',
    operands: [
      {
        joinOperator: 'AND',
        operands: [
          {
            attribute: 'itemType',
            operator: 'is',
            value: 'SERVICE'
          },
          {
            attribute: 'status',
            operator: 'is',
            value: 'ACTIVE'
          }
        ]
      },
      {
        joinOperator: 'AND',
        operands: [
          {
            joinOperator: 'AND',
            operands: [
              {
                attribute: 'serviceTypeItem.offeringType',
                operator: 'includes',
                value: ['BLOCK_HOUR', 'BLOCK_MONEY', 'SERVICE_BUNDLE']
              }
            ]
          }
        ]
      }
    ]
  },
  ONE_TIME: {
    joinOperator: 'AND',
    operands: [
      {
        joinOperator: 'AND',
        operands: [
          {
            attribute: 'itemType',
            operator: 'is',
            value: 'SERVICE'
          },
          {
            attribute: 'status',
            operator: 'is',
            value: 'ACTIVE'
          }
        ]
      },
      {
        joinOperator: 'AND',
        operands: [
          {
            joinOperator: 'AND',
            operands: [
              {
                attribute: 'serviceTypeItem.offeringType',
                operator: 'includes',
                value: ['BLOCK_HOUR', 'BLOCK_MONEY', 'SERVICE_BUNDLE']
              }
            ]
          }
        ]
      }
    ]
  },
  USAGE: {
    joinOperator: 'OR',
    operands: [
      {
        joinOperator: 'AND',
        operands: [
          {
            joinOperator: 'AND',
            operands: [
              {
                attribute: 'itemType',
                operator: 'is',
                value: 'SERVICE'
              },
              {
                attribute: 'status',
                operator: 'is',
                value: 'ACTIVE'
              },
              {
                attribute: 'serviceTypeItem.offeringType',
                operator: 'includes',
                value: ['QUANTITY']
              },
              {
                attribute: 'serviceTypeItem.defaultUsageType',
                operator: 'isNot',
                value: 'LATE_FEE'
              }
            ]
          }
        ]
      },
      {
        joinOperator: 'AND',
        operands: [
          {
            attribute: 'itemType',
            operator: 'is',
            value: 'PRODUCT'
          },
          {
            attribute: 'status',
            operator: 'is',
            value: 'ACTIVE'
          }
        ]
      }
    ]
  },
  TIME_AND_MATERIAL: null
};

export const BILLABLE_CONTRACT_DEFAULT_VALUE_MAP: Record<ContractType, any> = {
  SERVICE: {
    generateInvoice: false,
    frequencyType: 'RECURRING',
    recurringContract: {
      recurringMode: 'UPFRONT',
      frequencyDurationUnit: 'MONTH',
      frequencyInterval: 1
    },
    quantityCalculationType: 'FIXED',
    sellingPriceCalculationType: 'FIXED',
    billableSiteType: 'HQ'
  },
  USAGE: {
    generateInvoice: false,
    frequencyType: 'RECURRING',
    recurringContract: {
      recurringMode: 'UPFRONT',
      frequencyDurationUnit: 'MONTH',
      frequencyInterval: 1
    },
    quantityCalculationType: 'FIXED',
    sellingPriceCalculationType: 'FIXED',
    billableSiteType: 'HQ'
  },
  ONE_TIME: {
    generateInvoice: false,
    frequencyType: 'ONCE',
    quantityCalculationType: 'FIXED',
    sellingPriceCalculationType: 'FIXED'
  },
  TIME_AND_MATERIAL: undefined
};

export const ServiceCatalogConditionsBasedOnChargeItemType: Record<
  PackageTypeItemOfferingType,
  any
> = {
  BLOCK_HOUR: {
    joinOperator: 'AND',
    operands: [
      {
        attribute: 'status',
        operator: 'is',
        value: 'ACTIVE'
      },
      {
        attribute: 'itemType',
        operator: 'is',
        value: 'SERVICE'
      },
      {
        attribute: 'serviceTypeItem.offeringType',
        operator: 'is',
        value: 'HOURS'
      }
    ]
  },
  BLOCK_MONEY: {
    joinOperator: 'AND',
    operands: [
      {
        joinOperator: 'OR',
        operands: [
          {
            joinOperator: 'AND',
            operands: [
              {
                attribute: 'status',
                operator: 'is',
                value: 'ACTIVE'
              },
              {
                attribute: 'itemType',
                operator: 'is',
                value: 'SERVICE'
              },
              {
                attribute: 'serviceTypeItem.offeringType',
                operator: 'includes',
                value: ['QUANTITY']
              }
            ]
          },
          {
            joinOperator: 'AND',
            operands: [
              {
                attribute: 'itemType',
                operator: 'is',
                value: 'PRODUCT'
              },
              {
                attribute: 'status',
                operator: 'is',
                value: 'ACTIVE'
              }
            ]
          }
        ]
      },
      {
        joinOperator: 'AND',
        operands: [
          {
            attribute: 'serviceTypeItem.defaultUsageType',
            operator: 'isNot',
            value: 'LATE_FEE'
          }
        ]
      }
    ]
  },
  SERVICE_BUNDLE: {
    joinOperator: 'AND',
    operands: [
      {
        attribute: 'status',
        operator: 'is',
        value: 'ACTIVE'
      },
      {
        attribute: 'serviceTypeItem.offeringType',
        operator: 'notIncludes',
        value: 'SERVICE_BUNDLE'
      },
      {
        attribute: 'serviceTypeItem.redeemableServiceBundle.applicableType',
        operator: 'isNot',
        value: 'ALL_ITEMS'
      }
    ]
  }
};

const billableContractChanges = ['billableContract.changes'];

const recurringContractChanges = [
  'billableContract.recurringContract.recurringMode',
  'billableContract.recurringContract.frequencyDurationUnit',
  'billableContract.recurringContract.frequencyInterval',
  'billableContract.recurringContract.billingOffset'
];

export const propertiesToCheck = [
  {
    property: 'billableContract.billableSiteType',
    dependantProperties: [...billableContractChanges, 'billableContract.sites']
  },
  {
    property: 'billableContract.sites',
    dependantProperties: [...billableContractChanges]
  },
  {
    property: 'startDate',
    dependantProperties: [...billableContractChanges]
  },
  {
    property: 'billableContract.quantityCalculationType',
    dependantProperties: [...billableContractChanges]
  },
  {
    property: 'billableContract.sellingPriceCalculationType',
    dependantProperties: [...billableContractChanges]
  },
  {
    property: 'billableContract.provisionRule.provisionRuleId',
    dependantProperties: [...billableContractChanges]
  },
  {
    property:
      'billableContract.redeemableContract.carryForwardRemainingQuantity',
    dependantProperties: [
      'billableContract.redeemableContract.applicableType',
      'billableContract.redeemableContract.applicableHours'
    ]
  },
  {
    property: 'billableContract.redeemableContract.reminderThreshold',
    dependantProperties: [
      'billableContract.redeemableContract.applicableType',
      'billableContract.redeemableContract.applicableHours'
    ]
  },
  {
    property: 'billableContract.recurringContract.frequencyDurationUnit',
    dependantProperties: recurringContractChanges
  },
  {
    property: 'billableContract.recurringContract.frequencyInterval',
    dependantProperties: recurringContractChanges
  },
  {
    property: 'billableContract.recurringContract.recurringMode',
    dependantProperties: [
      ...recurringContractChanges,
      ...billableContractChanges
    ]
  },
  {
    property: 'billableContract.recurringContract.billingOffset',
    dependantProperties: recurringContractChanges
  },
  {
    property: 'billableContract.sellingPrice',
    dependantProperties: [
      'billableContract.sellingPrice.model',
      'billableContract.sellingPriceOverridden',
      'billableContract.sellingPriceCalculationType'
    ]
  }
];

export const templatePropertiesToCheck = [
  // {
  //   property: 'billableContract.quantityCalculationType',
  //   dependantProperties: billableContractChanges
  // },
  {
    property:
      'billableContract.redeemableContract.carryForwardRemainingQuantity',
    dependantProperties: [
      'billableContract.redeemableContract.applicableType',
      'billableContract.redeemableContract.applicableHours'
    ]
  },
  {
    property: 'billableContract.recurringContract.frequencyDurationUnit',
    dependantProperties: recurringContractChanges
  },
  {
    property: 'billableContract.recurringContract.frequencyInterval',
    dependantProperties: recurringContractChanges
  },
  {
    property: 'billableContract.recurringContract.recurringMode',
    dependantProperties: recurringContractChanges
  },
  {
    property: 'billableContract.recurringContract.billingOffset',
    dependantProperties: recurringContractChanges
  },
  {
    property: 'billableContract.sellingPrice',
    dependantProperties: [
      'billableContract.sellingPrice.model',
      'billableContract.sellingPriceOverridden',
      'billableContract.sellingPriceCalculationType'
    ]
  }
];
